import React, { useState } from 'react'
import { Button, Checkbox, Typography } from '@paintscout/ui'
import MenuIcon from '@material-ui/icons/Menu'
import { useSnackbar } from 'notistack'
import type { PainterNotificationPrefs } from './PainterScheduleView/types'
import PainterUserAvatar from './PainterUserAvatar'
import { usePainterNotificationsPanelStyles } from './PainterNotificationsPanel/usePainterNotificationsPanelStyles'

export type { PainterNotificationPrefs }

export interface PainterNotificationsPanelProps {
  userAvatarUrl?: string
  prefs?: PainterNotificationPrefs
  showTasksNotesFiles?: boolean
  hideHeader?: boolean
  onMenuClick?: () => void
  onChange?: (prefs: PainterNotificationPrefs) => void
}

const NOTIF_ROWS: { key: keyof PainterNotificationPrefs; label: string; section: 'jobs' | 'tasks' }[] = [
  { key: 'jobAssigned', label: 'When a job is assigned', section: 'jobs' },
  { key: 'jobUpdated', label: 'When a job is updated', section: 'jobs' },
  { key: 'jobCancelled', label: 'When a job is cancelled', section: 'jobs' },
  { key: 'taskAssigned', label: 'When a task is assigned', section: 'tasks' },
  { key: 'taskDueDaily', label: 'When upcoming tasks are due (daily reminder)', section: 'tasks' },
  { key: 'taskDueWeekly', label: 'When upcoming tasks are due (weekly reminder)', section: 'tasks' }
]

export default function PainterNotificationsPanel({
  userAvatarUrl,
  prefs = {},
  hideHeader = false,
  onMenuClick,
  onChange
}: PainterNotificationsPanelProps) {
  const classes = usePainterNotificationsPanelStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [state, setState] = useState<PainterNotificationPrefs>({
    jobAssigned: false,
    jobUpdated: false,
    jobCancelled: false,
    taskAssigned: false,
    taskDueDaily: false,
    taskDueWeekly: false,
    ...prefs
  })

  const dirty = NOTIF_ROWS.some(({ key }) => state[key] !== (prefs[key] ?? false))

  function toggle(key: keyof PainterNotificationPrefs) {
    setState((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function handleSave() {
    onChange?.(state)
    enqueueSnackbar('Notification preferences saved.', { variant: 'success' })
  }

  function handleDiscard() {
    setState({
      jobAssigned: false,
      jobUpdated: false,
      jobCancelled: false,
      taskAssigned: false,
      taskDueDaily: false,
      taskDueWeekly: false,
      ...prefs
    })
  }

  const jobRows = NOTIF_ROWS.filter((r) => r.section === 'jobs')
  const taskRows = NOTIF_ROWS.filter((r) => r.section === 'tasks')

  return (
    <div className={classes.root}>
      {!hideHeader && (
        <div className={classes.header}>
          <div className={classes.headerInner}>
            <button className={classes.menuBtn} onClick={onMenuClick} aria-label="menu">
              <MenuIcon className={classes.menuIcon} />
            </button>
            <span className={classes.headerTitle}>Notifications</span>
            <PainterUserAvatar className={classes.avatar} userAvatarUrl={userAvatarUrl} />
          </div>
        </div>
      )}
      <div className={classes.body}>
        <div className={classes.section}>
          <Typography variant="h4" className={classes.sectionTitle}>
            Jobs
          </Typography>
          {jobRows.map(({ key, label }) => (
            <div key={key} className={classes.row}>
              <Checkbox
                className={classes.checkbox}
                checked={!!state[key]}
                onChange={() => toggle(key)}
                color="primary"
              />
              <span className={classes.rowLabel}>{label}</span>
            </div>
          ))}
        </div>
        <div className={classes.section}>
          <Typography variant="h4" className={classes.sectionTitle}>
            Tasks
          </Typography>
          {taskRows.map(({ key, label }) => (
            <div key={key} className={classes.row}>
              <Checkbox
                className={classes.checkbox}
                checked={!!state[key]}
                onChange={() => toggle(key)}
                color="primary"
              />
              <span className={classes.rowLabel}>{label}</span>
            </div>
          ))}
        </div>
      </div>
      {dirty && (
        <div className={classes.footer}>
          <Button variant="text" onClick={handleDiscard}>
            Discard
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      )}
    </div>
  )
}
